import { Suspense, useEffect } from 'react';

import { useFlags } from '~/flags/useFlags';
import { useCurrentUser } from '~/hooks/useCurrentUser';
import NotFoundPage from '~/pages/404';
import { Route } from '~/routing/types';
import { useRouting } from '~/routing/useRouting';
import { MetricsService } from '~/services/MetricsService/MetricsService';

import PrivateRoute from './PrivateRoute';
import { resolveComponent } from './resolveComponent';

/**
 * This component tracks the app ready state and emits a metric when the component is mounted.
 * We *INTENTIONALLY* do not render it on public pages since they don't run any of the
 * expensive operations like checking for permissions/flags/authentication.
 */
const MountWatcher = () => {
  useEffect(() => {
    MetricsService.appReady();
  }, []);

  return null;
};

export function ResolvedComponent({ route }: { route: Route }) {
  const flags = useFlags();
  const { location } = useRouting();
  const currentUser = useCurrentUser();
  const { flag, path, isPublic, permissions, resolvers } = route;

  if (flag && !flags[flag]) {
    return <NotFoundPage />;
  }

  const context = {
    routeParams: location.state || {},
    user: currentUser,
  };

  const Resolved = resolveComponent(resolvers, context);

  if (isPublic) {
    return (
      <Suspense fallback={null}>
        <Resolved />
      </Suspense>
    );
  }

  return (
    <PrivateRoute key={path} permissions={permissions}>
      <Suspense fallback={null}>
        <Resolved />
      </Suspense>
      <MountWatcher />
    </PrivateRoute>
  );
}
